import { instanceKey } from "@/lib/instanceKey";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DataflowLastRun, DataflowSummary } from "@shared/types/dataflows";
import type { RunDetail, StartRunResponse } from "@shared/types/runs";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { DATAFLOWS_PATH, useDataflows } from "@/hooks/use-dataflows";
import { invalidateConnectionLists } from "@/hooks/use-overview";
import {
  RUN_POLL_INTERVAL_LIVE_MS,
  RUN_POLL_INTERVAL_MS,
  usePollWhile,
  useRun,
  useStartRun,
} from "@/hooks/use-runs";
import { newIdempotencyKey } from "@/lib/api";
import { useNotice, useNoticeState } from "@/lib/feedback/NoticeProvider";
import { listQueryOptions, useIsOnline } from "@/lib/listQuery";
import {
  LIST_INVALIDATION_INTERVAL_MS,
  startedRuns,
  useFeedTransport,
  useRunFeed,
  useStartedRunsVersion,
} from "@/lib/live/InstanceRunFeed";
import {
  activityAnnouncement,
  deriveRunActivity,
  emptyFrames,
  foldFrame,
  isActivityLive,
  withLiveLoads,
  type LiveRunFrames,
  type RunActivity,
} from "@/lib/runs/runActivity";
import type { StepVocabularyContext } from "@/lib/runs/stepVocabulary";

/**
 * One import's live activity for any surface (Session 10, P12; ux-s10.md §2).
 *
 * - **A list row** passes its `lastRun` as `seed` and fetches nothing: N rows cost no request.
 *   It folds this run's frames from the tab's one feed in place.
 * - **The editor and the run page** read `useRun(runId)`, which is NOT audited, and poll it
 *   every 5 seconds while the socket is down and every 15 while it is up, only while the
 *   import is still moving. A connection's detail (`GET /dataflows/:id`) is audited and is
 *   never read here.
 *
 * The feed is held only while the import is active, so a finished import opens no socket.
 */

/** How often the poll looks at the age of the last read. */
const POLL_TICK_MS = 1_000;

export interface UseRunActivityOptions {
  runId: string | null;
  /** The connection surface's connection; null on the run page (every row counts). */
  dataflowId: string | null;
  /** The list's `lastRun` projection; with a seed and no `fetchDetail`, nothing is fetched. */
  seed?: DataflowLastRun | null;
  /** Read the run detail as well (the editor, which also holds the list's seed). */
  fetchDetail?: boolean;
  vocabulary?: StepVocabularyContext;
  queuedAhead?: number | null;
  /**
   * The run id is the start response's from this tab: until the first read it is Queued, not
   * "loading" (S4). Never set it for a run id the screen merely read somewhere.
   */
  startedHere?: boolean;
  /**
   * False keeps this reader off the live feed: it polls every 5 seconds instead, as the
   * editor's last-run card did before the feed existed (`LastRunPanel` on its own).
   */
  live?: boolean;
}

export interface RunActivityResult extends RunActivity {
  detail: RunDetail | undefined;
}

export function useRunActivity({
  runId,
  dataflowId,
  seed,
  fetchDetail,
  vocabulary,
  queuedAhead,
  startedHere = false,
  live: subscribe = true,
}: UseRunActivityOptions): RunActivityResult {
  const { selectedInstance } = useTenantInstance();
  const feed = useRunFeed(selectedInstance?.id ?? null);
  const shouldFetch = fetchDetail ?? !seed;
  const run = useRun(shouldFetch ? runId : null);

  const [frames, setFrames] = useState<LiveRunFrames | null>(null);
  const liveFrames = frames && frames.runId === runId ? frames : null;

  const baseStatus = run.data?.runId === runId ? run.data?.status : seed?.status;
  const settled = baseStatus !== undefined && baseStatus !== "queued" && baseStatus !== "running";
  const frameSettled =
    liveFrames?.runStatus !== null &&
    liveFrames?.runStatus !== undefined &&
    liveFrames.runStatus !== "queued" &&
    liveFrames.runStatus !== "running";
  // Held while the import can still move: a run id with no read yet counts (the start
  // response's id before the first read), a finished one does not.
  const wantsFeed = subscribe && Boolean(runId) && !settled && !frameSettled;

  const transport = useFeedTransport(wantsFeed ? feed : null);

  useEffect(() => {
    if (!wantsFeed || !feed || !runId) return;
    const forRunId = runId;
    const off = feed.onFrame((frame) => {
      if (frame.runId !== forRunId) return;
      setFrames((previous) =>
        foldFrame(
          previous && previous.runId === forRunId ? previous : emptyFrames(forRunId),
          frame,
        ),
      );
    });
    const release = feed.acquire();
    return () => {
      off();
      release();
    };
  }, [wantsFeed, feed, runId]);

  const activity = useMemo(
    () =>
      deriveRunActivity({
        runId,
        dataflowId,
        detail: run.data,
        seed,
        frames: liveFrames,
        vocabulary,
        transport: wantsFeed ? transport.transport : "disabled",
        loading: shouldFetch && run.isLoading && !startedHere,
        unavailable: shouldFetch && run.isError && !seed,
        queuedAhead,
      }),
    [
      runId,
      dataflowId,
      run.data,
      seed,
      liveFrames,
      vocabulary,
      wantsFeed,
      transport.transport,
      shouldFetch,
      run.isLoading,
      run.isError,
      queuedAhead,
      startedHere,
    ],
  );

  // 5 s while the socket is not live, 15 s while it is (`use-runs.ts`). Decided by the age of
  // the last read on a one-second tick rather than by two timers, so a socket that opens and
  // drops in quick succession cannot keep restarting the fast poll and starve it.
  const polling = shouldFetch && Boolean(runId) && isActivityLive(activity);
  const live = transport.transport === "live";
  usePollWhile(polling, POLL_TICK_MS, () => {
    if (run.isFetching) return;
    const age = Date.now() - run.dataUpdatedAt;
    if (age >= (live ? RUN_POLL_INTERVAL_LIVE_MS : RUN_POLL_INTERVAL_MS)) void run.refetch();
  });

  // #1148: the detail's tables carry the load outcomes this reader has seen live, the same
  // per-table state its header counts, so a table counted as loaded reads Loaded in its row on
  // every surface that shows both (the connection page's Last run card, the run page).
  return useMemo(
    () => ({ ...activity, detail: withLiveLoads(run.data, liveFrames) }),
    [activity, run.data, liveFrames],
  );
}

/** What a live line last said, and for which run and moment (`announcementKey`). */
interface Said {
  runId: string | null;
  key: string;
  text: string;
}

const NOTHING_SAID: Said = { runId: null, key: "", text: "" };

/**
 * The moment an announcement belongs to: the run, its phase and, while running, its step. The
 * text is fixed for a moment once said, so a later read of the same moment (the run detail
 * after the list's seed, a connector name that arrives late, a rename) never says it again.
 * A running import whose step is not known yet (a seed without step counts) is at step 1: the
 * detail read that names step 1 is the same moment, not a second announcement (lead fix round).
 */
function announcementKey(activity: RunActivity): string {
  const step = activity.phase === "running" ? String(activity.stepIndex ?? 1) : "";
  return `${activity.runId ?? ""}|${activity.phase}|${step}`;
}

/**
 * The text of a polite live line for one connection's import (ux-s10.md §2, SF13; review r2,
 * B1'): it changes only on a step or phase change of an import this page has seen move.
 *
 * - It stays empty until this page has seen the import queued or running, or the import was
 *   started from this tab. Opening a connection whose last import failed days ago says nothing;
 *   the outcome is on the screen, not news.
 * - Once said, the words for a moment (`announcementKey`) are kept: the seed's "failed." is not
 *   followed by the detail's "failed at step 2 of 3." for the same failure, and the name is
 *   the one in use when the moment was said.
 * - `onlyStartedHere` (the list): only an import started from this tab is announced; every
 *   other row updates silently.
 */
export function useImportAnnouncement(
  activity: RunActivity,
  connectionName: string,
  { onlyStartedHere = false }: { onlyStartedHere?: boolean } = {},
): string {
  useStartedRunsVersion();
  const [said, setSaid] = useState<Said>(NOTHING_SAID);
  const runId = activity.runId;
  if (!runId) return "";
  const startedHere = startedRuns.startedHere(runId);
  if (onlyStartedHere && !startedHere) return "";
  const armed = said.runId === runId || startedHere || isActivityLive(activity);
  if (!armed) return "";
  const key = announcementKey(activity);
  if (said.runId === runId && said.key === key) return said.text;
  // Derived during render, as the editor seeds its form: the new moment is said in this very
  // render, and the state only remembers it, so the next render keeps the same words.
  const text = activityAnnouncement(activity, connectionName);
  setSaid({ runId, key, text });
  return text;
}

/** The newest of several run ids: the id carries its timestamp in the middle segment. */
export function newestRunId(...ids: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestStamp = "";
  for (const id of ids) {
    if (!id) continue;
    const stamp = id.split("-")[1] ?? "";
    if (best === null || stamp > bestStamp) {
      best = id;
      bestStamp = stamp;
    }
  }
  return best;
}

/**
 * True when this tab started an import for the connection that its list row does not show yet
 * (review r1): between the start answer and the list's next read, the row still names an older
 * run. The run names its connections at once (`createRunWithSteps` writes `last_run_id` in the
 * transaction that creates it, P7), so this lasts one list read. The list's "Import now" stays
 * disabled meanwhile, as the row itself already reads Queued.
 */
export function startedAheadOfList(
  row: Pick<DataflowSummary, "id" | "lastRunId" | "lastRun">,
  startedByDataflow: ReadonlyMap<string, string> = startedRuns.getSnapshot(),
): boolean {
  const started = startedByDataflow.get(row.id) ?? null;
  if (!started || started === row.lastRun?.runId || started === row.lastRunId) return false;
  return newestRunId(started, row.lastRun?.runId, row.lastRunId) === started;
}

export interface StartImportCallbacks {
  onSuccess?: (result: StartRunResponse) => void;
  onError?: (error: unknown) => void;
}

/** #1650: the key of a refused Import now for one connection (the editor and the list share it). */
export const importStartNoticeKey = (id: string): string => `import-start:${id}`;

/**
 * "Import now" from where the user is (the list row, the editor header): `useStartRun`. One start
 * per connection is in flight at a time (a synchronous lock, #1650: a press while one is in flight
 * is dropped, silently, as the button already reads pending), and each settled press mints a new
 * Idempotency-Key. The caller's callbacks run only while the calling component is mounted, so a
 * notice is never posted on the page the user moved to. On success
 * the run is marked as started from this tab (the row that announces, SF13; Queued before the
 * first read, S4) and the connections LIST is refreshed so the row reads Queued at once. The
 * list is not audited; a connection's detail is, and is never refreshed here.
 */
export function useStartImport(): {
  startImport: (dataflowId: string, callbacks?: StartImportCallbacks) => void;
  isPending: boolean;
  pendingDataflowId: string | null;
} {
  const start = useStartRun();
  const queryClient = useQueryClient();
  const { mutateAsync } = start;
  // #1650: a synchronous lock per connection. A second press before React has rendered the
  // pending state (a double click) finds the first still in flight and sends nothing, so the
  // server never sees two Idempotency-Keys for one intent and answers no 409 to the second.
  const inFlight = useRef<Set<string>>(new Set());
  // The hook-level effects below run whatever happens; the caller's callbacks only while mounted.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const startImport = useCallback(
    (dataflowId: string, callbacks: StartImportCallbacks = {}) => {
      if (inFlight.current.has(dataflowId)) return;
      inFlight.current.add(dataflowId);
      mutateAsync({ body: { dataflowId }, idempotencyKey: newIdempotencyKey() })
        .then(
          (result) => {
            startedRuns.mark(
              result.runId,
              result.dataflowIds && result.dataflowIds.length > 0
                ? result.dataflowIds
                : [dataflowId],
            );
            // #2094 review: the Overview's own copy of the list too, not only the Connections list.
            invalidateConnectionLists(queryClient);
            if (mounted.current) callbacks.onSuccess?.(result);
          },
          (error: unknown) => {
            if (mounted.current) callbacks.onError?.(error);
          },
        )
        .finally(() => inFlight.current.delete(dataflowId));
    },
    [mutateAsync, queryClient],
  );
  return {
    startImport,
    isPending: start.isPending,
    pendingDataflowId: start.isPending ? (start.variables?.body.dataflowId ?? null) : null,
  };
}

/**
 * How often an open editor with no import in progress re-reads the connections LIST, so an
 * import started later by the scheduler or another tab shows without a reload (review r1, S4).
 * The list is not audited; the same cadence as the feed's list refresh (SF3).
 */
export const EDITOR_LIST_REFRESH_MS = LIST_INVALIDATION_INTERVAL_MS;

/**
 * The editor's import (S4): the run is the newest of the start response from this tab, this
 * connection's row in the unaudited `GET /dataflows` list (which the feed refreshes on step
 * frames, at most every 30 s), and the `lastRunId` the audited detail read gave when the page
 * opened. The audited detail is never read again to find a run. The list row's `lastRun` is the
 * seed, and the run detail (`GET /runs/:id`, not audited) is read and polled while it moves.
 *
 * While nothing is moving, the editor holds no socket and re-reads that LIST every 30 s
 * (visible tab only, TanStack's default), so an import started elsewhere after the page opened
 * is found within 30 s; once found, the run's own reads and the feed take over and the list
 * poll stops (review r1, S4).
 */
export function useConnectionImport({
  dataflowId,
  detailRunId,
  vocabulary,
}: {
  dataflowId: string | null;
  detailRunId: string | null;
  vocabulary?: StepVocabularyContext;
}): RunActivityResult {
  useStartedRunsVersion();
  const { selectedInstance } = useTenantInstance();
  const list = useDataflows(Boolean(dataflowId));
  const row = dataflowId ? list.data?.find((entry) => entry.id === dataflowId) : undefined;
  const started = dataflowId ? startedRuns.runFor(dataflowId) : null;
  const runId = newestRunId(started, row?.lastRun?.runId, row?.lastRunId, detailRunId);
  const seed = row?.lastRun && row.lastRun.runId === runId ? row.lastRun : null;
  const activity = useRunActivity({
    runId,
    dataflowId,
    seed,
    fetchDetail: true,
    vocabulary,
    startedHere: runId !== null && runId === started,
  });
  // A second observer of the same list, enabled only while nothing moves, carries the poll;
  // the list is fresh for 30 s, so enabling it again costs no extra read.
  const moving = isActivityLive(activity);
  // #1398: off while the browser is offline, so the poll does not fail every tick; back online it
  // reads again at once when the list is stale.
  const online = useIsOnline();
  useQuery<DataflowSummary[]>({
    // One key, one rule: the Connections list's own entry (#1398).
    ...listQueryOptions,
    queryKey: instanceKey(DATAFLOWS_PATH, selectedInstance?.id),
    enabled: Boolean(dataflowId) && !moving && online,
    refetchInterval: EDITOR_LIST_REFRESH_MS,
  });
  return activity;
}

/**
 * #1650: a refused Import now (a 409 because another run holds the connection) says nothing true
 * once that run is live OR has moved on. `remember(id, runId)` records the connection's newest
 * run id when the refusal is posted; the notice under `importStartNoticeKey(id)` is dismissed when
 * the connection reads live, or its newest run is another one (the other run came and went before
 * this tab saw it live).
 */
export function useImportStartRefusals(
  entries: ReadonlyArray<{ id: string; runId: string | null; live: boolean }>,
): { remember: (id: string, runId: string | null) => void } {
  const { dismiss } = useNotice();
  const { notices } = useNoticeState();
  const refusedAt = useRef(new Map<string, string | null>());
  const remember = useCallback((id: string, runId: string | null) => {
    refusedAt.current.set(id, runId);
  }, []);
  useEffect(() => {
    for (const entry of entries) {
      if (!refusedAt.current.has(entry.id)) continue;
      const key = importStartNoticeKey(entry.id);
      if (!notices.some((n) => n.key === key)) continue;
      if (entry.live || entry.runId !== refusedAt.current.get(entry.id)) {
        dismiss(key);
        refusedAt.current.delete(entry.id);
      }
    }
  }, [entries, notices, dismiss]);
  return { remember };
}
